import { Building2, Check, ChevronDown, LayoutGrid, Package, ReceiptText, Truck, User, Users, Wallet, type LucideIcon } from 'lucide-react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { RBACAction, RBACModule } from '@numeo/types-core'
import { usePermissions } from 'hooks/usePermissions'
import { SearchCategory, SearchScope } from '../types/types'

interface ScopeMeta {
    scope: SearchScope
    label: string
    Icon: LucideIcon
    text: string
    softBg: string
    border: string
}

// Order + labels mirror the admin bar's CATEGORY_ORDER; `all` is the default scope.
// Colors follow the per-category accents used by SearchResultItem.
export const SCOPE_ITEMS: ScopeMeta[] = [
    {
        scope: 'all',
        label: 'All',
        Icon: LayoutGrid,
        text: 'text-neutral-500 dark:text-neutral-400',
        softBg: 'bg-neutral-100 dark:bg-neutral-800',
        border: 'border-neutral-200 dark:border-neutral-700',
    },
    {
        scope: SearchCategory.Loads,
        label: 'Loads',
        Icon: Package,
        text: 'text-blue-600 dark:text-blue-400',
        softBg: 'bg-blue-50 dark:bg-blue-900/30',
        border: 'border-blue-200 dark:border-blue-700',
    },
    {
        scope: SearchCategory.Invoices,
        label: 'Invoices',
        Icon: ReceiptText,
        text: 'text-green-600 dark:text-green-400',
        softBg: 'bg-green-50 dark:bg-green-900/30',
        border: 'border-green-200 dark:border-green-700',
    },
    {
        scope: SearchCategory.Trucks,
        label: 'Trucks',
        Icon: Truck,
        text: 'text-green-600 dark:text-green-400',
        softBg: 'bg-green-50 dark:bg-green-900/30',
        border: 'border-green-200 dark:border-green-700',
    },
    {
        scope: SearchCategory.Drivers,
        label: 'Drivers',
        Icon: User,
        text: 'text-amber-600 dark:text-amber-400',
        softBg: 'bg-amber-50 dark:bg-amber-900/30',
        border: 'border-amber-200 dark:border-amber-700',
    },
    {
        scope: SearchCategory.Contractors,
        label: 'Contractors',
        Icon: Users,
        text: 'text-slate-600 dark:text-slate-300',
        softBg: 'bg-slate-100 dark:bg-slate-800',
        border: 'border-slate-200 dark:border-slate-700',
    },
    {
        scope: SearchCategory.Payroll,
        label: 'Payroll',
        Icon: Wallet,
        text: 'text-green-600 dark:text-green-400',
        softBg: 'bg-green-50 dark:bg-green-900/30',
        border: 'border-green-200 dark:border-green-700',
    },
    {
        scope: SearchCategory.Brokers,
        label: 'Brokers',
        Icon: Building2,
        text: 'text-red-600 dark:text-red-400',
        softBg: 'bg-red-50 dark:bg-red-900/30',
        border: 'border-red-200 dark:border-red-700',
    },
    // Trips is intentionally omitted: searchTrips only matches a truckId (stringified ObjectId)
    // prefix or an exact trip _id, so scoping to Trips returns nothing for the truck numbers,
    // driver names or load numbers a dispatcher actually types. Trips still surface in the "All"
    // search — re-add a scope here once searchTrips widens its match fields.
]

export function getScopeMeta(scope: SearchScope): ScopeMeta {
    return SCOPE_ITEMS.find((item) => item.scope === scope) ?? SCOPE_ITEMS[0]
}

const MENU_WIDTH = 264

/** Scopes over Organization records. The server returns nothing for one the role cannot view, so the
 *  picker does not offer it. */
const SCOPE_MODULE: Partial<Record<SearchScope, RBACModule>> = {
    [SearchCategory.Drivers]: RBACModule.DRIVER_PROFILES,
    [SearchCategory.Trucks]: RBACModule.ASSET_PROFILES,
    [SearchCategory.Contractors]: RBACModule.CONTRACTOR_PROFILES,
    [SearchCategory.Brokers]: RBACModule.BROKER_PROFILES,
}

interface Props {
    scope: SearchScope
    onChange: (scope: SearchScope) => void
}

// The scope button lives inside the search bar; its menu is portaled to <body> with
// fixed positioning so the MUI Input can't clip it. Radix isn't a dependency here, so
// this mirrors the hand-rolled outside-click pattern already used by GlobalSearchBar.
export function ScopePicker({ scope, onChange }: Props) {
    const [open, setOpen] = useState(false)
    const [cursor, setCursor] = useState(0)
    const [pos, setPos] = useState({ top: 0, right: 0 })
    const btnRef = useRef<HTMLButtonElement>(null)
    const menuRef = useRef<HTMLDivElement>(null)
    const { hasPermission } = usePermissions()
    const scopeItems = useMemo(
        () =>
            SCOPE_ITEMS.filter(({ scope: s }) => {
                const module = SCOPE_MODULE[s]
                return !module || hasPermission(module, RBACAction.VIEW)
            }),
        [hasPermission]
    )

    const active = getScopeMeta(scope)
    const scoped = scope !== 'all'
    const ActiveIcon = active.Icon

    const openMenu = useCallback(() => {
        const rect = btnRef.current?.getBoundingClientRect()
        if (rect) setPos({ top: rect.bottom + 6, right: Math.max(8, window.innerWidth - rect.right) })
        setCursor(
            Math.max(
                0,
                scopeItems.findIndex((item) => item.scope === scope)
            )
        )
        setOpen(true)
    }, [scope, scopeItems])

    const select = useCallback(
        (next: SearchScope) => {
            onChange(next)
            setOpen(false)
            btnRef.current?.focus()
        },
        [onChange]
    )

    // Focus the menu on open so arrow/Enter/Escape land on it; close on outside click or viewport change.
    useEffect(() => {
        if (!open) return
        menuRef.current?.focus()
        const onPointerDown = (e: MouseEvent) => {
            const target = e.target as Node
            if (btnRef.current?.contains(target) || menuRef.current?.contains(target)) return
            setOpen(false)
        }
        const dismiss = () => setOpen(false)
        document.addEventListener('mousedown', onPointerDown)
        window.addEventListener('resize', dismiss)
        // Bubble phase (not capture): scroll doesn't bubble, so this fires only for page/window
        // scroll — the intent (reposition-dismiss). Capture phase would also catch nested scrolls
        // like the results list's scrollIntoView during keyboard nav and close the menu spuriously.
        window.addEventListener('scroll', dismiss)
        return () => {
            document.removeEventListener('mousedown', onPointerDown)
            window.removeEventListener('resize', dismiss)
            window.removeEventListener('scroll', dismiss)
        }
    }, [open])

    const onMenuKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            e.stopPropagation()
            setOpen(false)
            btnRef.current?.focus()
        } else if (e.key === 'ArrowDown') {
            e.preventDefault()
            setCursor((c) => (c + 1) % scopeItems.length)
        } else if (e.key === 'ArrowUp') {
            e.preventDefault()
            setCursor((c) => (c - 1 + scopeItems.length) % scopeItems.length)
        } else if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            select(scopeItems[cursor].scope)
        }
    }

    return (
        <>
            <button
                ref={btnRef}
                type="button"
                aria-haspopup="menu"
                aria-expanded={open}
                aria-label={`Search scope: ${active.label}`}
                onClick={() => (open ? setOpen(false) : openMenu())}
                className={`flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-semibold transition-colors ${
                    scoped
                        ? `border ${active.softBg} ${active.border} ${active.text}`
                        : 'border border-transparent text-neutral-600 hover:bg-neutral-100 dark:text-neutral-300 dark:hover:bg-neutral-800'
                }`}
            >
                <ActiveIcon className={`h-4 w-4 shrink-0 ${scoped ? active.text : 'text-neutral-500 dark:text-neutral-400'}`} />
                <span className="max-w-[84px] truncate">{active.label}</span>
                <ChevronDown className={`h-3.5 w-3.5 shrink-0 transition-transform ${open ? 'rotate-180' : ''} ${scoped ? active.text : 'text-neutral-400'}`} />
            </button>

            {open &&
                createPortal(
                    <div
                        ref={menuRef}
                        data-scope-portal
                        role="menu"
                        aria-label="Search scope"
                        tabIndex={-1}
                        onKeyDown={onMenuKeyDown}
                        style={{ position: 'fixed', top: pos.top, right: pos.right, width: MENU_WIDTH }}
                        className="z-[1300] rounded-xl border border-neutral-200 bg-white p-1.5 shadow-lg outline-none dark:border-neutral-700 dark:bg-neutral-900 dark:shadow-neutral-900/50"
                    >
                        <div className="px-2 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-neutral-400 dark:text-neutral-500">Search in</div>
                        {scopeItems.map((item, index) => {
                            const isActive = item.scope === scope
                            const ItemIcon = item.Icon
                            return (
                                <div key={item.scope}>
                                    <button
                                        type="button"
                                        role="menuitemradio"
                                        aria-checked={isActive}
                                        onClick={() => select(item.scope)}
                                        onMouseEnter={() => setCursor(index)}
                                        className={`flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left transition-colors ${
                                            index === cursor ? 'bg-neutral-100 dark:bg-neutral-800' : ''
                                        }`}
                                    >
                                        <span
                                            className={`grid h-7 w-7 shrink-0 place-items-center rounded-lg border ${item.softBg} ${item.border} ${item.text}`}
                                        >
                                            <ItemIcon className="h-4 w-4" />
                                        </span>
                                        <span className="flex-1 text-[13px] font-medium text-neutral-800 dark:text-neutral-100">{item.label}</span>
                                        <Check className={`h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400 ${isActive ? 'opacity-100' : 'opacity-0'}`} />
                                    </button>
                                    {item.scope === 'all' && <div className="my-1 h-px bg-neutral-200 dark:bg-neutral-700" />}
                                </div>
                            )
                        })}
                    </div>,
                    document.body
                )}
        </>
    )
}
